package com.devsusana.portfolio.pages.posts

import androidx.compose.runtime.Composable
import com.devsusana.portfolio.components.blog.*
import com.varabyte.kobweb.core.Page

@Page("/posts/building-home-tutor-pro")
@Composable
fun BuildingHomeTutorProPage() {
    ArticleScaffold(
        title = "Building HomeTutorPro: How I Mastered Agentic Programming",
        date = "2026-10-02",
        readingTime = "8 min read",
        tags = listOf("Android", "Agentic Coding", "Kotlin", "Gemini", "Clean Architecture"),
        isSpanish = false
    ) {
        ArticleSectionHeader("1. Introduction")

        ArticleParagraph(
            "As a Senior Android Engineer with over 7 years in production systems (including high-security banking and high-concurrency consumer apps), I was skeptical about early AI coding demos. Generic chat assistants often generated deprecated APIs, invented synthetic annotations, or broke Clean Architecture boundaries."
        )

        ArticleParagraph(
            "When developing HomeTutorPro, I chose a different path: Agentic Programming. Rather than using AI as a casual autocomplete tool, I designed an autonomous multi-agent pipeline with deterministic safeguards to audit architecture, generate unit test skeletons, and enforce code quality rules."
        )

        ArticleSectionHeader("2. The Architectural Challenge")

        ArticleParagraph("HomeTutorPro is a production-grade application featuring:")

        BulletItem("Layered Clean Architecture:", "Strict 3-tier separation (Domain, Data, Presentation) ensuring zero framework leakage into core business logic.")
        BulletItem("Offline-First Persistence:", "Room Database as single source of truth with complex relational queries.")
        BulletItem("Unidirectional Data Flow (UDF):", "Jetpack Compose with strict ViewModel state machines.")
        BulletItem("On-Device AI Inference:", "Google MediaPipe GenAI and Gemma running directly on device silicon.")

        ArticleParagraph(
            "When maintaining such a system solo, technical debt can compound quickly. I needed an automated mechanism to verify that:"
        )

        BulletItem("1. Pure Domain:", "Domain layers never leak Android SDK or framework dependencies.")
        BulletItem("2. Test Coverage:", "Every ViewModel state transition is covered by test assertions.")
        BulletItem("3. Contract Enforcement:", "Architecture guidelines specified in AGENTS.md are strictly followed.")

        ArticleSectionHeader("3. The 5-Agent Architecture Suite")

        ArticleParagraph("I designed 5 specialized local agents orchestrated by Python scripts and Gemini models:")

        CodeSnippet(
            code = """           ┌────────────────────────────────────────┐
           │        Local Agent Orchestrator        │
           └───────────────────┬────────────────────┘
       ┌───────────┬───────────┼───────────┬───────────┐
       ▼           ▼           ▼           ▼           ▼
   [Arch Aud]  [Security]  [Testing]    [KDoc]    [Refactor]
   • Clean     • Redacts   • JUnit &    • API     • Modernize
     Arch        PII         Flow         docs      Compose""",
            language = "Multi-Agent System"
        )

        ArticleSubHeader("Deterministic Safeguards")
        ArticleParagraph("Language models are probabilistic; software architecture cannot be. To make agents reliable:")

        BulletItem("AGENTS.md Contracts:", "Explicit structural guidelines defining allowed imports, package conventions, and naming rules.")
        BulletItem("Git Diff Scrapers:", "Agents only inspect and propose changes within the precise scope of modified files.")
        BulletItem("Compiler as Supreme Judge:", "No agent proposal is accepted unless ./gradlew test passes without warning.")

        ArticleSectionHeader("4. Handling Free-Tier LLM Rate Limits in Local Workflows")

        ArticleParagraph(
            "One major practical hurdle was LLM rate limiting during large codebase reviews. To make the pipeline resilient:"
        )

        BulletItem("Exponential Backoff with Jitter:", "Gracefully pauses and retries requests when encountering HTTP 429 rate limits.")
        BulletItem("Local AST & Symbol Caching:", "Cached Abstract Syntax Trees and symbol tables locally to prevent redundant prompt token consumption.")
        BulletItem("Deterministic Test Mocks:", "Built mock response fixtures for offline and deterministic test runs without hitting network APIs.")

        ArticleSectionHeader("5. Key Learnings & Architecture Impact")

        BulletItem("Specialization Beats Generalization:", "Five small, specialized agents with distinct system prompts outperform one monolithic general-purpose prompt by a wide margin.")
        BulletItem("Deterministic Bounds are Non-Negotiable:", "Always couple AI outputs with compilers, linters, and unit tests.")
        BulletItem("Productivity as a Force Multiplier:", "Routine boilerplate (KDocs, test doubles, dependency declarations) was reduced by 60%, allowing focus on high-impact architecture, memory management, and UX design.")

        CalloutBox {
            ArticleParagraph(
                "💡 Core Philosophy: \"Agents propose, compilers & tests dispose\". Agentic programming isn't about replacing developer judgment—it's about automating the cognitive burden of repetitive verification. By treating agent prompts as version-controlled code, our mobile architecture remains pristine even under rapid iteration."
            )
        }
    }
}

@Page("/posts/es/building-home-tutor-pro")
@Composable
fun BuildingHomeTutorProPageEs() {
    ArticleScaffold(
        title = "Construyendo HomeTutorPro: Cómo Dominé la Programación Agéntica",
        date = "2026-10-02",
        readingTime = "8 min de lectura",
        tags = listOf("Android", "Agentic Coding", "Kotlin", "Gemini", "Clean Architecture"),
        isSpanish = true
    ) {
        ArticleSectionHeader("1. Introducción")

        ArticleParagraph(
            "Como Senior Android Engineer con más de 7 años de trayectoria en sistemas en producción (abarcando entornos bancarios de alta seguridad y aplicaciones de consumo masivo), al principio observaba con escepticismo las primeras herramientas de generación de código con IA. Los asistentes conversacionales genéricos con frecuencia sugerían APIs obsoletas, inventaban anotaciones inexistentes o rompían las fronteras fundamentales de Clean Architecture."
        )

        ArticleParagraph(
            "Al desarrollar HomeTutorPro, decidí abordar el problema desde una óptica distinta: la Programación Agéntica. En lugar de emplear la IA como un simple autocompletador pasivo, estructuré un pipeline de agentes autónomos locales gobernados por salvaguardas deterministas para auditar la arquitectura, generar esqueletos de pruebas unitarias y garantizar el cumplimiento riguroso de las guías de estilo."
        )

        ArticleSectionHeader("2. El Desafío Arquitectónico")

        ArticleParagraph("HomeTutorPro es un proyecto de nivel productivo que incorpora:")

        BulletItem("Clean Architecture Rigurosa:", "Separación estricta en 3 capas (Dominio, Datos, Presentación) garantizando cero dependencias del framework en la lógica de negocio.")
        BulletItem("Persistencia Offline-First:", "Base de datos Room como única fuente de verdad con consultas relacionales complejas.")
        BulletItem("Flujo Unidireccional (UDF):", "Jetpack Compose con máquinas de estado rigurosas en ViewModels.")
        BulletItem("IA en el Dispositivo:", "Inferencia local mediante Google MediaPipe GenAI y modelos Gemma sobre el hardware del terminal.")

        ArticleParagraph(
            "Mantener una arquitectura de esta envergadura de forma individual puede acumular deuda técnica con rapidez si no existen controles automáticos. Necesitaba un sistema que asegurase de forma implacable que:"
        )

        BulletItem("1. Dominio Puro:", "Las capas de dominio jamás contengan importaciones o dependencias del Android SDK.")
        BulletItem("2. Cobertura de Tests:", "Cada transición de estado en los ViewModels cuente con aserciones de test automatizadas.")
        BulletItem("3. Cumplimiento de Contratos:", "Las directrices de arquitectura recogidas en el contrato AGENTS.md se cumplan sin excepciones.")

        ArticleSectionHeader("3. La Suite de 5 Agentes Especializados")

        ArticleParagraph("Diseñé 5 agentes locales específicos orquestados mediante scripts en Python y modelos Gemini:")

        CodeSnippet(
            code = """           ┌────────────────────────────────────────┐
           │         Orquestador de Agentes         │
           └───────────────────┬────────────────────┘
       ┌───────────┬───────────┼───────────┬───────────┐
       ▼           ▼           ▼           ▼           ▼
   [Auditor]  [Seguridad]  [Testing]    [KDoc]    [Refactor]
   • Clean     • Oculta    • Tests      • Docs    • Moderniza
     Arch        PII         JUnit        API       Compose""",
            language = "Sistema Multi-Agente"
        )

        ArticleSubHeader("Salvaguardas Deterministas")
        ArticleParagraph("Los modelos de lenguaje son probabilísticos; la arquitectura de software no puede serlo. Para dotar al flujo de fiabilidad absoluta:")

        BulletItem("Contratos en AGENTS.md:", "Especificaciones estructurales explícitas que definen qué imports están autorizados, convenciones de paquetes y reglas de nomenclatura.")
        BulletItem("Inspectores de Git Diff:", "Los agentes únicamente analizan y proponen modificaciones dentro del ámbito exacto de los archivos que han cambiado.")
        BulletItem("El Compilador como Juez Supremo:", "Ninguna propuesta generada por un agente se da por válida a menos que ./gradlew test compile y pase todos los tests sin advertencias.")

        ArticleSectionHeader("4. Gestión de Límites de Cuota (Rate Limits) en Flujos de Desarrollo")

        ArticleParagraph(
            "Uno de los principales retos prácticos consistió en gestionar los límites de tasa (HTTP 429) de las APIs de LLMs durante revisiones de código extensas. Para inmunizar el flujo ante interrupciones:"
        )

        BulletItem("Backoff Exponencial con Jitter:", "Pausa y reintenta las solicitudes de forma controlada ante límites de cuota temporales.")
        BulletItem("Caché Local de AST y Símbolos:", "Almacenamiento en caché de Árboles de Sintaxis Abstracta y tablas de símbolos para evitar llamadas redundantes a la API.")
        BulletItem("Mocks Deterministas:", "Accesorios de respuesta simulados para permitir ejecuciones de verificación en local completamente offline.")

        ArticleSectionHeader("5. Aprendizajes Clave e Impacto en la Productividad")

        BulletItem("La Especialización Supera a la Generalización:", "Cinco agentes compactos y especializados con system prompts diferenciados rinden con una precisión muy superior a un único prompt omnisciente.")
        BulletItem("Los Límites Deterministas son Innegociables:", "El código producido por IA debe contrastarse siempre contra compiladores, linters y baterías de pruebas unitarias.")
        BulletItem("Multiplicador de Productividad:", "La carga de tareas mecánicas y repetitivas (redacción de KDocs, dobles de test, declaraciones de dependencias) se redujo en un 60%, permitiendo focalizar el esfuerzo de ingeniería en la arquitectura, la optimización de memoria y la experiencia de usuario.")

        CalloutBox {
            ArticleParagraph(
                "💡 Filosofía Central: \"Los agentes proponen, los compiladores y los tests disponen\". La programación agéntica no consiste en delegar el criterio técnico, sino en automatizar la carga cognitiva de la verificación repetitiva. Tratando los prompts como código versionado, la arquitectura se mantiene limpia incluso a un ritmo alto de desarrollo."
            )
        }
    }
}

